import { useEffect, useRef, useState } from "react";

/**
 * NativeAR — wraps Google's <model-viewer> and exposes a launchAR() handle.
 *
 * Strategy:
 *   - Android Chrome / Scene-Viewer supported devices get TRUE scene-anchored AR
 *     (the <model-viewer ar> element launches Scene Viewer with the .glb).
 *   - iOS Safari, desktop, anything without AR support → returns a flag so the
 *     caller can fall back to the simulated camera AR instead.
 *
 * The model is tinted to the selected colour by mutating the PBR material's
 * baseColorFactor on every colour change. This is what gives you 20 visually
 * distinct AR placements without 20 separate .glb files.
 */
const MODEL_SRC = "/models/kisubika-tote.glb";

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  const v = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(v, 16);
  const srgb = [
    ((n >> 16) & 255) / 255,
    ((n >> 8) & 255) / 255,
    (n & 255) / 255,
  ];
  // glTF baseColorFactor is in linear space — convert from sRGB or the
  // material renders too bright in <model-viewer>.
  return srgb.map((c) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
};

export const NativeAR = ({ color, hidden = true }, ref) => {
  const mvRef = useRef(null);
  const [arSupported, setArSupported] = useState(false);

  // Tint the model whenever the selected colour changes
  useEffect(() => {
    const mv = mvRef.current;
    if (!mv) return;
    const apply = () => {
      try {
        const rgb = hexToRgb(color.hex);
        const mat = mv.model?.materials?.[0]?.pbrMetallicRoughness;
        if (mat) mat.setBaseColorFactor([...rgb, 1]);
      } catch {
        /* model not ready yet */
      }
    };
    if (mv.loaded) apply();
    mv.addEventListener("load", apply, { once: false });
    return () => mv.removeEventListener("load", apply);
  }, [color]);

  // Detect AR capability once the element is in the DOM
  useEffect(() => {
    const mv = mvRef.current;
    if (!mv) return;
    const update = () => setArSupported(Boolean(mv.canActivateAR));
    update();
    mv.addEventListener("ar-status", update);
    return () => mv.removeEventListener("ar-status", update);
  }, []);

  return (
    <model-viewer
      ref={mvRef}
      src={MODEL_SRC}
      data-testid="model-viewer"
      ar
      ar-modes="scene-viewer webxr quick-look"
      ar-scale="fixed"
      camera-controls
      auto-rotate
      shadow-intensity="1"
      exposure="1.05"
      environment-image="neutral"
      style={{
        width: hidden ? "1px" : "100%",
        height: hidden ? "1px" : "100%",
        opacity: hidden ? 0 : 1,
        position: hidden ? "absolute" : "relative",
        pointerEvents: hidden ? "none" : "auto",
      }}
      data-ar-supported={arSupported ? "true" : "false"}
    />
  );
};

/** Stand-alone helper: try to activate AR on the hidden model-viewer.
 * Returns true if Scene Viewer / WebXR was launched, false otherwise. */
export async function tryActivateAR() {
  const el = document.querySelector('model-viewer[data-testid="model-viewer"]');
  if (!el) return false;
  if (!el.canActivateAR) return false;
  try {
    await el.activateAR();
    return true;
  } catch {
    return false;
  }
}
